/**
 * schedule-contract — the SINGLE typed builder for `CronSchedule` triggers.
 *
 * Red line #9 (单一真相源): every `CronSchedule` a producer creates (cron_manage,
 * cloud_task_manage, enterprise HTTP task authoring) is normalized through
 * `buildCronSchedule` before it reaches the store / wire. The ONE thing this
 * builder guarantees that a hand-rolled object literal cannot: an `every` trigger
 * ALWAYS carries an `anchorMs` stamped at creation time.
 *
 * ── Why the anchor matters (C1: anchorless `every` over-fire) ─────────────────
 * The stateless server dispatcher (`computeLatestDueFireMs`) asks each tick "what
 * is the latest firing point ≤ now?". For `every` that is `anchor + k·everyMs`. An
 * `every` trigger WITHOUT an anchor has no stable grid: the dispatcher used to fall
 * back to `nowMs` as the anchor, so every tick produced a *new* firing point (now),
 * a new `(task×user×scheduledAt)` tuple, and a fresh billed execution — the task
 * fired every tick instead of every `everyMs`. Stamping `anchorMs` once at creation
 * pins the grid so the same firing point repeats until the interval elapses.
 *
 * This module is fs-free, network-free, dependency-free (it deliberately does NOT
 * validate cron-expression *semantics* — that needs the `croner` parser which lives
 * in core's `isValidCronExpr`; producers call that separately before this builder).
 */
/** Minimum interval for an `every` schedule (matches the dispatcher's floor). */
const MIN_EVERY_MS = 5_000;
/** Friendly, non-raw error for a malformed `CronSchedule` payload — mirrors
 *  `CronExecutionParseError`'s posture (agent/user-safe, no internal host names). */
export class CronScheduleParseError extends Error {
    constructor(message) {
        super(message);
        this.name = "CronScheduleParseError";
    }
}
/**
 * The ONLY sanctioned constructor for a `CronSchedule`. Validates the discriminated
 * union structurally, strips unknown keys, and — the load-bearing behaviour —
 * stamps `anchorMs = now` on an `every` trigger that lacks one. An `every` that
 * already carries an `anchorMs` (existing rows re-hydrated, or an explicit anchor)
 * is preserved verbatim, so re-building is idempotent and existing schedules are
 * unchanged.
 *
 * Throws `CronScheduleParseError` on an invalid input (unknown kind, non-finite
 * `at` / `everyMs`, empty `expr`). Does NOT check cron-expression validity — callers
 * that need it run `isValidCronExpr` (core) first.
 */
export function buildCronSchedule(input, opts) {
    if (!input || typeof input !== "object") {
        throw new CronScheduleParseError("触发配置无效：期望一个对象");
    }
    const kind = input.kind;
    if (kind === "at") {
        const at = input.at;
        if (typeof at !== "string" || !at) {
            throw new CronScheduleParseError('触发配置 "at" 须为非空字符串（ISO 时间）');
        }
        if (!Number.isFinite(new Date(at).getTime())) {
            throw new CronScheduleParseError('触发配置 "at" 须为合法 ISO 时间');
        }
        return { kind: "at", at };
    }
    if (kind === "every") {
        const everyRaw = input.everyMs;
        if (typeof everyRaw !== "number" || !Number.isFinite(everyRaw)) {
            throw new CronScheduleParseError('触发配置 "everyMs" 须为有限数字');
        }
        const everyMs = Math.max(MIN_EVERY_MS, Math.floor(everyRaw));
        const anchorRaw = input.anchorMs;
        // Preserve an explicit / re-hydrated anchor; otherwise stamp creation time so
        // the firing grid is stable from now on (C1 anchorless over-fire fix).
        const anchorMs = typeof anchorRaw === "number" && Number.isFinite(anchorRaw)
            ? Math.max(0, Math.floor(anchorRaw))
            : opts?.now ?? Date.now();
        return { kind: "every", everyMs, anchorMs };
    }
    if (kind === "cron") {
        const expr = input.expr;
        if (typeof expr !== "string" || !expr) {
            throw new CronScheduleParseError('触发配置 "expr" 须为非空 cron 表达式');
        }
        const tz = input.tz;
        if (tz !== undefined && typeof tz !== "string") {
            throw new CronScheduleParseError('触发配置 "tz" 须为字符串');
        }
        return tz !== undefined ? { kind: "cron", expr, tz } : { kind: "cron", expr };
    }
    throw new CronScheduleParseError(`触发配置 kind 无效（须为 at | every | cron，收到 ${JSON.stringify(kind)}）`);
}
//# sourceMappingURL=schedule-contract.js.map